import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Coins, TrendingUp, Lock, Unlock, Gift, Clock, Percent, 
  DollarSign, ArrowUpRight, ArrowDownRight, Wallet, Shield,
  Sparkles, Timer, BarChart3, CheckCircle
} from 'lucide-react';

import { API } from '@/config/api';

export default function Staking() {
  const { user } = useAuth();
  const [pools, setPools] = useState([]);
  const [myStakes, setMyStakes] = useState([]);
  const [rewardsSummary, setRewardsSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stakeModalOpen, setStakeModalOpen] = useState(false);
  const [unstakeModalOpen, setUnstakeModalOpen] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [selectedStake, setSelectedStake] = useState(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [lockPeriod, setLockPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('pools');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [poolsRes, stakesRes, summaryRes] = await Promise.all([
        fetch(`${API}/staking/pools`),
        fetch(`${API}/staking/my-stakes`, { credentials: 'include' }),
        fetch(`${API}/staking/rewards-summary`, { credentials: 'include' })
      ]);

      if (poolsRes.ok) setPools(await poolsRes.json());
      if (stakesRes.ok) setMyStakes(await stakesRes.json());
      if (summaryRes.ok) setRewardsSummary(await summaryRes.json());
    } catch (e) {
      console.error('Fetch error:', e);
    }
    setLoading(false);
  };

  const handleStake = async () => {
    if (!stakeAmount || !selectedPool) return;

    try {
      const res = await fetch(`${API}/staking/stake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          asset: selectedPool.asset,
          amount: parseFloat(stakeAmount),
          lock_period: parseInt(lockPeriod)
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Successfully staked ${stakeAmount} ${selectedPool.asset}!`);
        setStakeModalOpen(false);
        setStakeAmount('');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to stake');
      }
    } catch (e) {
      toast.error('Failed to stake');
    }
  };

  const handleUnstake = async () => {
    if (!selectedStake) return;

    try {
      const res = await fetch(`${API}/staking/unstake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stake_id: selectedStake.stake_id })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Unstaked! Received ${data.total_received} ${selectedStake.asset}`);
        setUnstakeModalOpen(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Cannot unstake yet');
      }
    } catch (e) {
      toast.error('Failed to unstake');
    }
  };

  const getEffectiveAPY = (baseAPY, period) => {
    const multipliers = { 7: 0.5, 30: 1.0, 90: 1.5, 180: 2.0 };
    return (baseAPY * (multipliers[period] || 1)).toFixed(1);
  };

  const formatTVL = (tvl) => {
    if (tvl >= 1000000) return `$${(tvl / 1000000).toFixed(1)}M`;
    if (tvl >= 1000) return `$${(tvl / 1000).toFixed(0)}K`;
    return `$${tvl.toFixed(0)}`;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Card className="bg-[#14120f] border-[#1c1814] p-8 text-center">
          <Coins className="h-16 w-16 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Staking</h2>
          <p className="text-gray-400 mb-4">Sign in to stake your assets and earn rewards</p>
          <Button onClick={() => window.location.href = '/'} className="bg-[#D4AF37] text-black">
            Sign In
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#14120f] via-[#1c1814] to-[#14120f] border-b border-[#1c1814]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Coins className="h-8 w-8 text-[#D4AF37]" />
            <h1 className="text-3xl font-bold">Staking</h1>
            <Badge className="bg-emerald-500/20 text-emerald-400">Earn Rewards</Badge>
          </div>
          <p className="text-gray-400">Stake your crypto assets and earn passive income</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-[#14120f] to-[#1c1814] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Staked</p>
                  <p className="text-2xl font-bold text-white">
                    ${rewardsSummary?.total_staked_value?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <Wallet className="h-8 w-8 text-[#D4AF37]" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#14120f] to-[#1c1814] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Rewards</p>
                  <p className="text-2xl font-bold text-emerald-400">
                    ${rewardsSummary?.total_rewards_earned?.toFixed(6) || '0.000000'}
                  </p>
                </div>
                <Gift className="h-8 w-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#14120f] to-[#1c1814] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Active Stakes</p>
                  <p className="text-2xl font-bold text-white">{rewardsSummary?.active_stakes || 0}</p>
                </div>
                <Lock className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#14120f] to-[#1c1814] border-[#1c1814]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Avg APY</p>
                  <p className="text-2xl font-bold text-purple-400">{rewardsSummary?.avg_apy || 6.5}%</p>
                </div>
                <Percent className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-[#14120f] border border-[#1c1814]">
            <TabsTrigger value="pools" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Sparkles className="h-4 w-4 mr-2" /> Staking Pools
            </TabsTrigger>
            <TabsTrigger value="my-stakes" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Lock className="h-4 w-4 mr-2" /> My Stakes ({myStakes.length})
            </TabsTrigger>
          </TabsList>

          {/* Pools Tab */}
          <TabsContent value="pools" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pools.map(pool => (
                <Card key={pool.asset} className="bg-[#14120f] border-[#1c1814] hover:border-[#D4AF37]/50 transition-colors overflow-hidden">
                  <div className="h-1 bg-gradient-to-r from-[#D4AF37] to-emerald-500" />
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#1c1814] flex items-center justify-center">
                          <span className="text-2xl font-bold text-[#D4AF37]">{pool.asset.charAt(0)}</span>
                        </div>
                        <div>
                          <CardTitle className="text-white">{pool.asset}</CardTitle>
                          <p className="text-sm text-gray-400">Flexible Staking</p>
                        </div>
                      </div>
                      <Badge className="bg-emerald-500/20 text-emerald-400">
                        Up to {(pool.apy * 2).toFixed(1)}% APY
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-[#0a0a0f] rounded-lg">
                        <p className="text-xs text-gray-500">Base APY</p>
                        <p className="text-lg font-bold text-emerald-400">{pool.apy}%</p>
                      </div>
                      <div className="p-3 bg-[#0a0a0f] rounded-lg">
                        <p className="text-xs text-gray-500">TVL</p>
                        <p className="text-lg font-bold text-white">{formatTVL(pool.tvl)}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Min Stake</span>
                        <span className="text-white">{pool.min_stake} {pool.asset}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Stakers</span>
                        <span className="text-white">{pool.stakers.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {pool.lock_periods.map(period => (
                        <Badge key={period} variant="outline" className="border-gray-700 text-gray-400">
                          {period}d → {getEffectiveAPY(pool.apy, period)}%
                        </Badge>
                      ))}
                    </div>

                    <Button 
                      className="w-full bg-[#D4AF37] text-black hover:bg-[#c9a432]"
                      onClick={() => { setSelectedPool(pool); setStakeModalOpen(true); }}
                    >
                      <Lock className="h-4 w-4 mr-2" /> Stake {pool.asset}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* My Stakes Tab */}
          <TabsContent value="my-stakes" className="space-y-4">
            {myStakes.length === 0 ? (
              <Card className="bg-[#14120f] border-[#1c1814]">
                <CardContent className="p-12 text-center">
                  <Lock className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Active Stakes</h3>
                  <p className="text-gray-400 mb-4">Start staking to earn passive rewards on your crypto</p>
                  <Button onClick={() => setActiveTab('pools')} className="bg-[#D4AF37] text-black">
                    View Staking Pools
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myStakes.map(stake => (
                  <Card key={stake.stake_id} className="bg-[#14120f] border-[#1c1814]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-[#1c1814] flex items-center justify-center">
                            <span className="text-2xl font-bold text-[#D4AF37]">{stake.asset.charAt(0)}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-white text-lg">{stake.amount} {stake.asset}</span>
                              <Badge className="bg-blue-500/20 text-blue-400">
                                <Lock className="h-3 w-3 mr-1" /> {stake.lock_days} days
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-400">
                              Started {new Date(stake.start_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-gray-500">APY</p>
                            <p className="font-bold text-emerald-400">{stake.apy.toFixed(1)}%</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Earned</p>
                            <p className="font-bold text-[#D4AF37]">+{stake.earned_rewards.toFixed(6)}</p>
                          </div>
                          <div className="text-center min-w-[100px]">
                            <p className="text-xs text-gray-500 mb-1">Lock Progress</p>
                            {stake.can_unstake ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400">
                                <CheckCircle className="h-3 w-3 mr-1" /> Unlocked
                              </Badge>
                            ) : (
                              <div>
                                <Progress 
                                  value={((stake.lock_days - stake.days_remaining) / stake.lock_days) * 100} 
                                  className="h-2 bg-[#1c1814]"
                                />
                                <p className="text-xs text-gray-500 mt-1">{stake.days_remaining}d left</p>
                              </div>
                            )}
                          </div>
                          
                          <Button 
                            variant={stake.can_unstake ? "default" : "outline"}
                            disabled={!stake.can_unstake}
                            onClick={() => { setSelectedStake(stake); setUnstakeModalOpen(true); }}
                            className={stake.can_unstake 
                              ? "bg-emerald-500 hover:bg-emerald-600" 
                              : "border-gray-700 text-gray-500 cursor-not-allowed"
                            }
                          >
                            <Unlock className="h-4 w-4 mr-1" /> 
                            {stake.can_unstake ? 'Unstake' : 'Locked'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Stake Modal */}
      <Dialog open={stakeModalOpen} onOpenChange={setStakeModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Lock className="h-5 w-5 text-[#D4AF37]" />
              Stake {selectedPool?.asset}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Lock your {selectedPool?.asset} to earn staking rewards
            </DialogDescription>
          </DialogHeader>
          
          {selectedPool && (
            <div className="space-y-4 py-4">
              {/* Amount Input */}
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Amount to Stake</label>
                <div className="relative">
                  <Input
                    type="number"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                    placeholder={`Min: ${selectedPool.min_stake}`}
                    className="bg-[#0a0a0f] border-[#1c1814] text-white pr-16"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {selectedPool.asset}
                  </span>
                </div>
              </div>

              {/* Lock Period Selection */}
              <div className="space-y-2">
                <label className="text-sm text-gray-400">Lock Period</label>
                <Select value={lockPeriod} onValueChange={setLockPeriod}>
                  <SelectTrigger className="bg-[#0a0a0f] border-[#1c1814] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14120f] border-[#1c1814]">
                    {selectedPool.lock_periods.map(period => (
                      <SelectItem key={period} value={period.toString()}>
                        {period} days - {getEffectiveAPY(selectedPool.apy, period)}% APY
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Summary */}
              <div className="p-4 bg-[#0a0a0f] rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Effective APY</span>
                  <span className="text-emerald-400 font-bold">{getEffectiveAPY(selectedPool.apy, parseInt(lockPeriod))}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Lock Until</span>
                  <span className="text-white">
                    {new Date(Date.now() + parseInt(lockPeriod) * 86400000).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Est. Rewards</span>
                  <span className="text-[#D4AF37]">
                    +{((parseFloat(stakeAmount) || 0) * (parseFloat(getEffectiveAPY(selectedPool.apy, parseInt(lockPeriod))) / 100) * (parseInt(lockPeriod) / 365)).toFixed(6)} {selectedPool.asset}
                  </span>
                </div>
              </div>

              {/* Warning */}
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Timer className="h-5 w-5 text-yellow-400 mt-0.5" />
                  <p className="text-xs text-gray-400">
                    Your assets will be locked for {lockPeriod} days. You cannot unstake early.
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setStakeModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button 
              onClick={handleStake} 
              disabled={!stakeAmount || parseFloat(stakeAmount) < (selectedPool?.min_stake || 0)}
              className="bg-[#D4AF37] text-black"
            >
              <Lock className="h-4 w-4 mr-1" /> Stake Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unstake Modal */}
      <Dialog open={unstakeModalOpen} onOpenChange={setUnstakeModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Unlock className="h-5 w-5 text-emerald-400" />
              Unstake {selectedStake?.asset}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Withdraw your staked assets and earned rewards
            </DialogDescription>
          </DialogHeader>
          
          {selectedStake && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-[#0a0a0f] rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Staked Amount</span>
                  <span className="text-white font-bold">{selectedStake.amount} {selectedStake.asset}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Earned Rewards</span>
                  <span className="text-emerald-400 font-bold">+{selectedStake.earned_rewards.toFixed(6)} {selectedStake.asset}</span>
                </div>
                <div className="border-t border-[#1c1814] pt-3 flex justify-between">
                  <span className="text-gray-400">Total to Receive</span>
                  <span className="text-[#D4AF37] font-bold text-lg">
                    {(selectedStake.amount + selectedStake.earned_rewards).toFixed(6)} {selectedStake.asset}
                  </span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setUnstakeModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleUnstake} className="bg-emerald-500 hover:bg-emerald-600">
              <Unlock className="h-4 w-4 mr-1" /> Confirm Unstake
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
